/**
 * Auth0 SSO configuration — mirrors the env contract platform-core-iac's web
 * app uses (NEXT_PUBLIC_* → VITE_*). The login flow is BFF-mediated: we send
 * the user to the API's `/Auth0/User/Authorize` endpoint, which bounces them
 * through Auth0 and back to our `/auth/callback` with the token in the URL hash.
 *
 * Because every Signature app shares the `.signatureaviation.com` token cookie,
 * a user already signed into any of them is transparently signed in here (SSO).
 */

/** BFF authorize endpoint, e.g. `https://api.signatureaviation.com/Auth0/User/Authorize`. */
export const AUTH_LOGIN_URL = import.meta.env.VITE_AUTH_LOGIN_URL ?? "";

/** Auth0 tenant domain, e.g. `signatureaviation.us.auth0.com`. Used for logout. */
export const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN ?? "";

/** Auth0 SPA client id — used to build the Auth0 logout URL. */
export const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID ?? "";

/**
 * Which auth flow the SPA drives, mirroring the API's four modes:
 *   - "disabled"  — no login (dev admin).
 *   - "simple"    — single shared credential via a /login form (interim stopgap).
 *   - "auth0"     — full Auth0 SSO redirect flow.
 *   - "skyagent"  — SkyAgent JWT handoff redirect flow.
 */
export type AuthMode = "disabled" | "simple" | "auth0" | "skyagent";

/**
 * Build-time hint from env. `VITE_AUTH_MODE` wins; else inferred from the legacy
 * `VITE_AUTH_DISABLED`. This is only a *fallback* — at runtime the app asks the
 * API (`GET /auth/mode`) for the authoritative mode (see setRuntimeAuthMode), so
 * the SPA can never drift out of sync with how the backend is actually configured.
 */
const BUILD_AUTH_MODE: AuthMode = (() => {
  const explicit = String(import.meta.env.VITE_AUTH_MODE ?? "").toLowerCase();
  if (explicit === "disabled" || explicit === "simple" || explicit === "auth0" || explicit === "skyagent") {
    return explicit;
  }
  const disabled = String(import.meta.env.VITE_AUTH_DISABLED ?? "").toLowerCase() === "true";
  return disabled ? "disabled" : "auth0";
})();

/** Runtime mode, populated from the API. Null until the first /auth/mode call. */
let runtimeAuthMode: AuthMode | null = null;

/** Called once the API's real mode is known (or to clear the override in tests). */
export function setRuntimeAuthMode(mode: AuthMode | null): void {
  runtimeAuthMode = mode;
}

/** The authoritative auth mode: the API's mode if known, else the build hint. */
export function getAuthMode(): AuthMode {
  return runtimeAuthMode ?? BUILD_AUTH_MODE;
}

/** True when there's no login at all (dev admin). */
export function isAuthDisabled(): boolean {
  return getAuthMode() === "disabled";
}

/** Build-time hint, exported for reference/tests. Prefer getAuthMode() for behaviour. */
export const AUTH_MODE = BUILD_AUTH_MODE;

/** The cross-domain cookie name the whole platform shares. Must match the BFF. */
export const TOKEN_COOKIE_NAME = "token";

/** sessionStorage key that survives the Auth0 → BFF → back round-trip. */
export const AUTH_RETURN_PATH_KEY = "auth_return_path";

/** sessionStorage key for the CSRF state nonce (skyagent mode). */
export const AUTH_STATE_KEY = "auth_state_nonce";

export function isLocalhost(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h === "0.0.0.0";
}

/** SkyAgent handoff endpoint, e.g. `https://<skyagent>/api/auth/ops-handoff`. */
export const SKYAGENT_HANDOFF_URL = import.meta.env.VITE_SKYAGENT_HANDOFF_URL ?? "";

/** SkyAgent root URL for logout redirect, e.g. `https://<skyagent>`. */
export const SKYAGENT_URL = import.meta.env.VITE_SKYAGENT_URL ?? "";

/** Where Auth0/the BFF should send the user back to after token exchange. */
export function getCallbackUrl(): string {
  if (typeof window === "undefined") return "/auth/callback";
  return `${window.location.origin}/auth/callback`;
}
